/**
 * The other door.
 *
 * `sanitize.ts` guards markup arriving as HTML — a pasted design, a loaded
 * `HTML` template, a reopened draft. But a document can also arrive already
 * parsed: a `TIPTAP_JSON` template, a saved signature, a sequence step's stored
 * body. Those skip the HTML door entirely and land in the editor as nodes.
 *
 * That was survivable while the schema could only hold what it modelled — there
 * was nowhere in a paragraph to put an `onclick`. The fallback tier changed it:
 * `htmlBlock` stores every attribute an element carried and `htmlRaw` stores
 * markup verbatim, so a stored document can now describe things a stored
 * document could not describe before. The same check therefore has to hold on
 * this side too.
 *
 * React blocks most of it downstream — it drops `on*` props and rewrites
 * `javascript:` URLs — but `restoreRawMarkup` writes its bytes into the
 * finished string after React is done, so raw markup has no such backstop.
 */
import type { JSONContent } from "@tiptap/core";

import {
  DANGEROUS_CSS,
  DANGEROUS_ELEMENTS,
  SAFE_URL,
  URL_ATTRIBUTES,
  sanitizeForEmail,
} from "./sanitize";

/** Stored element attributes, minus anything that can execute or fetch. */
function sanitizeStoredAttributes(
  attributes: Record<string, unknown>,
): Record<string, unknown> {
  const safe: Record<string, unknown> = {};
  for (const [name, value] of Object.entries(attributes)) {
    const lower = name.toLowerCase();
    if (lower.startsWith("on")) continue;
    if (
      URL_ATTRIBUTES.includes(lower) &&
      !SAFE_URL.test(String(value).trim())
    ) {
      continue;
    }
    safe[name] = value;
  }
  return safe;
}

/**
 * A stored style map, minus the declarations that run.
 *
 * Dropped one at a time rather than wholesale, which the HTML side cannot do:
 * there the value is still a string and taking it apart is how a bypass gets
 * through, but here the parser has already split it into discrete declarations.
 */
function sanitizeStyleMap(
  style: Record<string, unknown>,
): Record<string, unknown> {
  const safe: Record<string, unknown> = {};
  for (const [property, value] of Object.entries(style)) {
    if (DANGEROUS_CSS.test(`${property}:${String(value)}`)) continue;
    if (hasUnsafeUrl(String(value))) continue;
    safe[property] = value;
  }
  return safe;
}

/**
 * A `url(...)` in a declaration answers to the same schemes as a URL attribute.
 *
 * A section's background image lives here, so a `blob:` address — valid only
 * in the tab that made it — would otherwise reach a later send, which can't
 * upload a file it doesn't have and fails outright.
 */
function hasUnsafeUrl(value: string): boolean {
  for (const [, , target] of value.matchAll(/url\(\s*(['"]?)(.*?)\1\s*\)/gi)) {
    if (!SAFE_URL.test(target.trim())) return true;
  }
  return false;
}

/**
 * Verbatim markup, re-checked.
 *
 * Parsed and run through the same pass the HTML door uses, so a `<style>` block
 * and an Outlook conditional are held to one standard however they arrived.
 * Without a DOM — server render, a test environment — the markup is judged on
 * its text instead, and dropped rather than trusted.
 */
function sanitizeRawMarkup(html: string): string {
  if (typeof DOMParser === "undefined") {
    const looksDangerous =
      DANGEROUS_CSS.test(html) ||
      new RegExp(
        `<\\s*(${DANGEROUS_ELEMENTS.split(",").join("|")})\\b`,
        "i",
      ).test(html);
    return looksDangerous ? "" : html;
  }
  const document = new DOMParser().parseFromString(
    `<body>${html}</body>`,
    "text/html",
  );
  sanitizeForEmail(document.body);
  return document.body.innerHTML;
}

function sanitizeNode(node: JSONContent): JSONContent {
  const next: JSONContent = { ...node };

  if (next.attrs) {
    const attrs: Record<string, unknown> = { ...next.attrs };

    const stored = attrs.attributes;
    if (stored && typeof stored === "object") {
      attrs.attributes = sanitizeStoredAttributes(
        stored as Record<string, unknown>,
      );
    }

    const style = attrs.style;
    if (style && typeof style === "object") {
      attrs.style = sanitizeStyleMap(style as Record<string, unknown>);
    }

    // A URL does not only arrive inside `attributes`. A link mark keeps its
    // `href` as a named attr, and so do the typed blocks that model one —
    // image's `src`, button's `href`.
    for (const name of URL_ATTRIBUTES) {
      const value = attrs[name];
      if (typeof value === "string" && !SAFE_URL.test(value.trim())) {
        delete attrs[name];
      }
    }

    if (typeof attrs.html === "string") {
      attrs.html = sanitizeRawMarkup(attrs.html);
    }

    next.attrs = attrs;
  }

  // A mark carries attributes too — a link's `href` is the one that matters.
  if (next.marks) {
    next.marks = next.marks.map((mark) => sanitizeNode(mark) as typeof mark);
  }

  if (next.content) {
    next.content = next.content.map(sanitizeNode);
  }

  return next;
}

/**
 * Make a stored document safe to open.
 *
 * Returns a new document; the input is left alone, so a caller holding the
 * original — a template row, a draft in cache — still has what it had.
 */
export function sanitizeEmailDocument<T extends JSONContent | undefined>(
  content: T,
): T {
  if (!content) return content;
  return sanitizeNode(content) as T;
}
